import { useContext } from 'react'

import { useTranslation } from 'react-i18next'
import { useDataScope } from 'scrivito'

import { MobileFilterContext } from './MobileFilterContext'
import { MobileFilterSelect } from './MobileFilterSelect'
import { Option } from '@/Atomic/Molecules/Inputs/Select/types'
import { SimpleSelect } from '@/Atomic/Molecules/SimpleSelect'
import { useFilterValues } from '@/Components/FormContext'
import { DataClassName } from '@/Data/const'
import { SchemaAttributes } from '@/Data/types'
import { getSchemaName } from '@/utils/dataClassMap'
import { getSchema } from '@/utils/getSchema'
import { getLocalizedSchemaAttributes } from '@/utils/schemaLocalization'

const ALL_TYPES = 'all'

interface TypeFilterProps {
  /**
   * Entity whose schema provides the types. Defaults to the data class of the
   * surrounding data scope — pass it explicitly where the scope belongs to
   * another entity, for example the activity tab of a contact.
   */
  dataClassName?: string
  filterKey?: string
  renderLabel?: (hasValue: boolean, onClear: () => void) => React.ReactNode
  group?: string
}

export const TypeFilter: React.FC<TypeFilterProps> = ({
  dataClassName: dataClassNameProp,
  filterKey = 'type',
  renderLabel,
  group,
}) => {
  const { t } = useTranslation()
  const dataScope = useDataScope()
  const { values, setValues } = useFilterValues()
  const { isMobile } = useContext(MobileFilterContext)

  const dataClassName = dataClassNameProp || dataScope?.dataClassName() || ''

  const { schema } = getSchema(getSchemaName(dataClassName))

  // Return null if schema is not loaded or doesn't have attributes
  if (!schema) return null

  const schemaAttributes = getLocalizedSchemaAttributes(
    schema?.get('attributes') as SchemaAttributes,
  )

  const typeFilters = schemaAttributes?.['type']?.enumValues

  if (!typeFilters) return null

  // Activities offer no "All types" entry: the design asks for a "Select type"
  // placeholder, which stands for the unrestricted list and is reset via the
  // field's "Clear" link (CRM-1272).
  const hasAllTypesOption = dataClassName !== DataClassName.ACTIVITY

  const options: Option[] = [
    ...(hasAllTypesOption
      ? [{ label: t('common.filter.allTypes'), value: ALL_TYPES }]
      : []),
    ...typeFilters.map((filter) => ({
      label: filter.title,
      value: filter.value,
    })),
  ]

  const currentValue =
    values[filterKey] || (hasAllTypesOption ? ALL_TYPES : undefined)
  const selectedOption = options.find((o) => o.value === currentValue)
  const hasValue = !!currentValue && currentValue !== ALL_TYPES

  const handleChange = (newValue: Option) => {
    const val = newValue.value as string
    if (val === ALL_TYPES) {
      setValues((prev) => {
        const { [filterKey]: _removed, ...rest } = prev
        return rest
      })
    } else {
      setValues((prev) => ({ ...prev, [filterKey]: val }))
    }
  }

  const handleClear = () => {
    setValues((prev) => {
      const { [filterKey]: _removed, ...rest } = prev
      return rest
    })
  }

  if (isMobile) {
    return (
      <>
        {renderLabel ? (
          renderLabel(hasValue, handleClear)
        ) : (
          <label className="mobile-filter-label">
            {t('common.filter.type')}
          </label>
        )}
        <MobileFilterSelect
          name="type-select"
          options={options}
          value={selectedOption}
          onChange={handleChange}
          placeholder={t('common.filter.selectType')}
          title={t('common.filter.type')}
        />
      </>
    )
  }

  return (
    <>
      {renderLabel && renderLabel(hasValue, handleClear)}
      <SimpleSelect
        name="type-select"
        options={options}
        value={selectedOption}
        onChange={handleChange}
        placeholder={t('common.filter.selectType')}
        group={group}
      />
    </>
  )
}
